import React from 'react';
import { Card, Col, Drawer, Form, Input, InputNumber, Modal, Row, Select, Space, Tabs, Typography, message } from 'antd';
import type { ColumnType } from 'antd/es/table';

import { deleteData, fetchSqlDataWithQuery, insertHeaderWithItems } from '../../../../services/api';
import { useGetMatLogisticShipments } from '../../../../hooks/genericHooks';
import StandardTable, { createActionColumn } from '../../../../components/common/StandardTable';
import ExcelUploadButton from '../../../../components/common/ExcelUploadButton';
import MatClntMasterMaterialSelect from '../../../../components/modals/MatClntMasterMaterialSelect';
import CurrencySelect from '../../../../components/modals/CurrencySelect';
import FileUploadTool, { FileUploadToolHandle } from '../../../../components/common/FileUploadTool';
import FileDownloadTool from '../../../../components/common/FileDownloadTool';
import Button from '../../../../components/common/Button';
import {
  buildItemsFromPlRows,
  buildPackagesFromParsedRows,
  getNextItemId,
  packageTypeOptions,
  parsePlAttachment,
  type PackageItemDraft,
  type PackageType,
} from '../helpers/matLogPlAttachmentImport';

const { Title, Text } = Typography;

type TransportType = 'sea' | 'air' | 'road' | 'rail' | 'other' | 'tbd';
type PackageStatus = 'pending' | 'cancelled' | 'in transit' | 'delivered';

const transportTypeOptions: TransportType[] = ['sea', 'air', 'road', 'rail', 'other', 'tbd'];
const statusOptions: PackageStatus[] = ['pending', 'cancelled', 'in transit', 'delivered'];

type PackageHeaderDraft = {
  po_number?: string | null;
  package_id: string;
  shipment_id?: string | null;
  previous_shipment_id?: string | null;
  transport_type: TransportType;
  package_type: PackageType;
  status: PackageStatus;
  net_weight_kg?: number | null;
  gross_weight_kg?: number | null;
  height_cm?: number | null;
  width_cm?: number | null;
  length_cm?: number | null;
  final_destination?: string | null;
  remarks?: string | null;
  upload_path?: string | null;
};

type FormValues = {
  packages: PackageHeaderDraft[];
};

type PackageItemFormValues = Omit<PackageItemDraft, 'key'>;

type BulkCommonFields = Pick<
  PackageHeaderDraft,
  'shipment_id' | 'previous_shipment_id' | 'transport_type' | 'status' | 'final_destination' | 'remarks'
>;

export type MatLogPackageUpsertProps = {
  mode?: 'create' | 'edit';
  editIds?: string[];
};

const MatLogPackageUpsert: React.FC<MatLogPackageUpsertProps> = ({ mode = 'create', editIds }) => {
  const isEditMode = mode === 'edit';
  const [form] = Form.useForm<FormValues>();
  const [itemForm] = Form.useForm<PackageItemFormValues>();
  const [bulkForm] = Form.useForm<BulkCommonFields>();
  const drawerBodyRef = React.useRef<HTMLDivElement | null>(null);
  const mountedRef = React.useRef(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState<boolean>(() => (mode === 'edit' && Boolean((editIds || []).length)));
  const [createAllState, setCreateAllState] = React.useState<{
    running: boolean;
    total: number;
    done: number;
    current?: string | null;
  }>({ running: false, total: 0, done: 0, current: null });
  const [editHeaderIdByIndex, setEditHeaderIdByIndex] = React.useState<Record<number, number>>({});
  const [createdPackageIds, setCreatedPackageIds] = React.useState<string[]>([]);
  const [itemsByIndex, setItemsByIndex] = React.useState<Record<number, PackageItemDraft[]>>({});
  const [activePackageIndex, setActivePackageIndex] = React.useState<number>(0);
  const [activeTabKey, setActiveTabKey] = React.useState<string>('0');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editingItemKey, setEditingItemKey] = React.useState<string | null>(null);
  const [editingPackageIndex, setEditingPackageIndex] = React.useState<number | null>(null);
  const [materialModalOpen, setMaterialModalOpen] = React.useState(false);
  const [materialSelectTarget, setMaterialSelectTarget] = React.useState<'parent_item_code' | 'itemcode'>('itemcode');
  const [currencyModalOpen, setCurrencyModalOpen] = React.useState(false);
  const [currencyModalAnchorRect, setCurrencyModalAnchorRect] = React.useState<DOMRect | null>(null);

  const plInputRef = React.useRef<HTMLInputElement>(null);
  const [plFileName, setPlFileName] = React.useState<string>('');
  const [plLoading, setPlLoading] = React.useState(false);
  const [plConflictOpen, setPlConflictOpen] = React.useState(false);
  const [plConflictIds, setPlConflictIds] = React.useState<string[]>([]);
  const plConflictResolverRef = React.useRef<((choice: 'merge' | 'replace' | 'cancel') => void) | null>(null);
  const [packageTabKeys, setPackageTabKeys] = React.useState<number[]>(() => (mode === 'edit' ? [] : [0]));
  const [tabTick, setTabTick] = React.useState(0);

  const fileUploadRefsByIndex = React.useRef<Record<number, FileUploadToolHandle | null>>({});
  const [uploadPathByIndex, setUploadPathByIndex] = React.useState<Record<number, string>>({});

  const askPlConflictChoice = React.useCallback(async (ids: string[]) => {
    return await new Promise<'merge' | 'replace' | 'cancel'>((resolve) => {
      plConflictResolverRef.current = resolve;
      setPlConflictIds(ids);
      setPlConflictOpen(true);
    });
  }, []);

  const createdPackageIdSet = React.useMemo(() => {
    return new Set((createdPackageIds || []).map((x) => String(x || '').trim()));
  }, [createdPackageIds]);
  const showTotalTab = packageTabKeys.length > 1;

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const normalizedEditIds = React.useMemo(() => {
    return (editIds || []).map((x) => String(x || '').trim()).filter((x) => x);
  }, [editIds]);

  const editLoadSeqRef = React.useRef(0);
  const loadEditPackages = React.useCallback(
    async (ids: string[]) => {
      const seq = (editLoadSeqRef.current += 1);
      setInitialLoading(true);
      try {
        // Fast path: one request for all headers+items (requires ALLOW_RAW_SQL=true on backend).
        // Falls back to concurrency-limited per-table fetch if disabled.
        const tryBulkQuery = async () => {
          const numericIds = (ids || []).map((x) => String(x || '').trim()).filter((x) => /^\d+$/.test(x));
          if (numericIds.length !== (ids || []).length) {
            throw new Error('Invalid package id list (expected numeric ids)');
          }
          const idList = numericIds.join(','); // safe because digits-only
          const query = `
            SELECT
              p.*,
              COALESCE(
                jsonb_agg(to_jsonb(i) ORDER BY i.item_id) FILTER (WHERE i.id IS NOT NULL),
                '[]'::jsonb
              ) AS items
            FROM public.mat_logistic_packages p
            LEFT JOIN public.mat_logistic_package_items i
              ON i.package_id = p.package_id
            WHERE p.id IN (${idList})
            GROUP BY p.id
            ORDER BY array_position(ARRAY[${idList}]::bigint[], p.id);
          `;
          const rows: any[] = await fetchSqlDataWithQuery(query);
          return Array.isArray(rows) ? rows : [];
        };

        // Prefer the bulk query for both single and multi edit.
        const bulkRows = await tryBulkQuery();
        if (!mountedRef.current || seq !== editLoadSeqRef.current) return;
        if (!bulkRows.length) {
          throw new Error('No packages returned from bulk query');
        }

        const nextPackages: PackageHeaderDraft[] = [];
        const nextItemsByIndex: Record<number, PackageItemDraft[]> = {};
        const nextHeaderIdByIndex: Record<number, number> = {};

        bulkRows.forEach((hdr: any, idx: number) => {
          const pid = String(hdr.package_id || '').trim();
          if (!pid) return;

          nextPackages.push({
            package_id: pid,
            po_number: hdr.po_number ?? null,
            shipment_id: hdr.shipment_id ? String(hdr.shipment_id) : null,
            previous_shipment_id: hdr.previous_shipment_id ? String(hdr.previous_shipment_id) : null,
            transport_type: (hdr.transport_type ? String(hdr.transport_type) : 'tbd') as TransportType,
            package_type: (hdr.package_type ? String(hdr.package_type) : 'tbd') as any,
            status: (hdr.status ? String(hdr.status) : 'pending') as any,
            upload_path: hdr.upload_path ? String(hdr.upload_path) : null,
            net_weight_kg: toNumberOrNull((hdr as any).net_weight_kg),
            gross_weight_kg: toNumberOrNull((hdr as any).gross_weight_kg),
            height_cm: toNumberOrNull((hdr as any).height_cm),
            width_cm: toNumberOrNull((hdr as any).width_cm),
            length_cm: toNumberOrNull((hdr as any).length_cm),
            final_destination: hdr.final_destination ?? null,
            remarks: hdr.remarks ?? null,
          });

          const hid = Number(hdr.id);
          if (Number.isFinite(hid)) nextHeaderIdByIndex[idx] = hid;

          const itemsArrRaw = hdr.items;
          const itemsArr =
            Array.isArray(itemsArrRaw)
              ? itemsArrRaw
              : typeof itemsArrRaw === 'string'
                ? (() => {
                    try {
                      const parsed = JSON.parse(itemsArrRaw);
                      return Array.isArray(parsed) ? parsed : [];
                    } catch {
                      return [];
                    }
                  })()
                : [];

          nextItemsByIndex[idx] = (itemsArr || []).map((r: any, i: number) => ({
            key: `db_${String(r.id ?? i)}`,
            item_id: r.item_id ?? null,
            parent_item_code: r.parent_item_code ?? null,
            itemcode: r.itemcode ?? null,
            size_text: r.size_text ?? null,
            short_description: r.short_description ?? null,
            long_description: r.long_description ?? null,
            notes: r.notes ?? null,
            hs_code: r.hs_code ?? null,
            country_of_origin: r.country_of_origin ?? null,
            unit: r.unit ?? null,
            unit_price: r.unit_price ?? null,
            currency: r.currency ?? 'USD',
            quantity: r.quantity ?? null,
          }));
        });

        if (!nextPackages.length) {
          throw new Error('Bulk query returned rows but package_id was missing');
        }

        if (!mountedRef.current || seq !== editLoadSeqRef.current) return;

        form.setFieldValue('packages', nextPackages);
        setUploadPathByIndex(() => {
          const next: Record<number, string> = {};
          nextPackages.forEach((p, idx) => {
            next[idx] = String((p as any)?.upload_path || '').trim();
          });
          return next;
        });
        setItemsByIndex(nextItemsByIndex);
        setEditHeaderIdByIndex(nextHeaderIdByIndex);
        setCreatedPackageIds([]); // edit mode should not lock the UI
        setPackageTabKeys(Array.from({ length: nextPackages.length }, (_, i) => i));
        setActivePackageIndex(0);
        setActiveTabKey(nextPackages.length > 1 ? 'total' : '0');
        bulkForm.resetFields();
        setTabTick((t) => t + 1);
      } catch (e: any) {
        message.error(e?.message || 'Failed to load package');
      } finally {
        if (mountedRef.current && seq === editLoadSeqRef.current) {
          setInitialLoading(false);
        }
      }
    },
    [bulkForm, form]
  );

  React.useEffect(() => {
    if (!isEditMode) return;
    if (!normalizedEditIds.length) return;
    loadEditPackages(normalizedEditIds);
  }, [isEditMode, loadEditPackages, normalizedEditIds]);

  const toNumberOrNull = React.useCallback((v: any) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(String(v));
    return Number.isFinite(n) ? n : null;
  }, []);

  const toUpperOrSame = React.useCallback((v: any) => {
    if (typeof v !== 'string') return v;
    return v.toUpperCase();
  }, []);

  // Keep tabs controlled by React state, because AntD Form may not always
  // re-render the parent component for programmatic `packages[]` updates.
  const packages = React.useMemo(() => {
    return ((form.getFieldValue('packages') || []) as PackageHeaderDraft[]) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, tabTick, packageTabKeys.length]);
  React.useEffect(() => {
    const current = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
    if (!current.length) {
      if (isEditMode) {
        // In edit mode we wait for server-loaded package; avoid flashing an empty draft package.
        return;
      }
      form.setFieldValue('packages', [
        {
          package_id: '',
          po_number: null,
          shipment_id: null,
          previous_shipment_id: null,
          transport_type: 'tbd',
          package_type: 'tbd' as PackageType,
          status: 'pending',
          upload_path: null,
          net_weight_kg: null,
          gross_weight_kg: null,
          height_cm: null,
          width_cm: null,
          length_cm: null,
          final_destination: null,
          remarks: null,
        },
      ]);
      setActivePackageIndex(0);
      setItemsByIndex({ 0: [] });
      setUploadPathByIndex({ 0: '' });
      setPackageTabKeys([0]);
      setActiveTabKey('0');
      return;
    }
    setPackageTabKeys(Array.from({ length: current.length }, (_, i) => i));

    setActivePackageIndex((idx) => {
      if (idx < 0) return 0;
      if (idx >= current.length) return Math.max(0, current.length - 1);
      return idx;
    });

    setItemsByIndex((prev) => {
      const next = { ...(prev || {}) };
      for (let i = 0; i < current.length; i += 1) {
        if (!next[i]) next[i] = [];
      }
      return next;
    });
  }, [form, isEditMode, tabTick, packageTabKeys.length]);

  const activeItems = React.useMemo(() => {
    if (activeTabKey === 'total') return [];
    return (itemsByIndex || {})[activePackageIndex] || [];
  }, [activePackageIndex, activeTabKey, itemsByIndex]);

  const totalsByPackageIndex = React.useMemo(() => {
    const toNum = (v: any) => {
      const n = typeof v === 'number' ? v : Number(String(v ?? 0));
      return Number.isFinite(n) ? n : 0;
    };
    const round3 = (n: number) => {
      if (!Number.isFinite(n)) return 0;
      return Math.round(n * 1000) / 1000;
    };
    const computeFreightTon = (idx: number) => {
      const gross = toNum(form.getFieldValue(['packages', idx, 'gross_weight_kg']));
      const l = toNum(form.getFieldValue(['packages', idx, 'length_cm']));
      const w = toNum(form.getFieldValue(['packages', idx, 'width_cm']));
      const h = toNum(form.getFieldValue(['packages', idx, 'height_cm']));
      const weightTon = gross / 1000;
      const volumeM3 = (l * w * h) / 1_000_000;
      return round3(Math.max(weightTon || 0, volumeM3 || 0));
    };
    const computeTotalPrice = (idx: number) => {
      const rows = (itemsByIndex || {})[idx] || [];
      return rows.reduce((acc, r) => {
        const up = toNum(r.unit_price);
        const qty = toNum(r.quantity);
        return acc + up * qty;
      }, 0);
    };

    const out: Record<number, { freightTon: number; totalPrice: number }> = {};
    (packageTabKeys || []).forEach((idx) => {
      out[idx] = { freightTon: computeFreightTon(idx), totalPrice: computeTotalPrice(idx) };
    });
    return out;
  }, [form, itemsByIndex, packageTabKeys, tabTick]);

  const totalAll = React.useMemo(() => {
    const entries = Object.values(totalsByPackageIndex || {});
    const round3 = (n: number) => {
      if (!Number.isFinite(n)) return 0;
      return Math.round(n * 1000) / 1000;
    };
    const freightTon = round3(entries.reduce((acc, x) => acc + (x.freightTon || 0), 0));
    const totalPrice = entries.reduce((acc, x) => acc + (x.totalPrice || 0), 0);
    return { freightTon, totalPrice };
  }, [totalsByPackageIndex]);

  const applyCommonFieldsToAll = React.useCallback(() => {
    const values = bulkForm.getFieldsValue();
    const normalizeStringOrNull = (v: any) => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      return s ? s : null;
    };
    const norm = (v: any) => String(v ?? '').trim().toLowerCase();

    const payload: Partial<BulkCommonFields> = {};
    const shipmentId = normalizeStringOrNull(values.shipment_id);
    const prevShipmentId = normalizeStringOrNull(values.previous_shipment_id);
    const transportType = normalizeStringOrNull(values.transport_type);
    const status = normalizeStringOrNull(values.status);
    const finalDest = normalizeStringOrNull(values.final_destination);
    const remarks = normalizeStringOrNull(values.remarks);

    if (shipmentId) payload.shipment_id = shipmentId;
    if (prevShipmentId) payload.previous_shipment_id = prevShipmentId;
    if (transportType) payload.transport_type = transportType as any;
    if (status) payload.status = status as any;
    if (finalDest) payload.final_destination = finalDest;
    if (remarks) payload.remarks = remarks;

    const fields = Object.keys(payload) as Array<keyof BulkCommonFields>;
    if (!fields.length) {
      message.warning('Nothing to apply (all common fields are empty)');
      return;
    }

    if (!packageTabKeys.length) {
      message.warning('No packages found to apply common fields to');
      return;
    }

    const current = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
    const willOverride: Array<{ idx: number; field: string; from: any; to: any }> = [];
    const targetIndexes = (packageTabKeys || []).filter((idx) => {
      const pid = String(current?.[idx]?.package_id || '').trim();
      if (!pid) return true; // allow applying to drafts without ID yet
      return !createdPackageIdSet.has(pid);
    });

    const shipmentChanging = payload.shipment_id !== undefined;
    const statusChanging = payload.status !== undefined;
    const transportChanging = payload.transport_type !== undefined;
    const editableIndexes = (shipmentChanging || statusChanging || transportChanging)
      ? targetIndexes.filter((idx) => {
          const st = norm((current?.[idx] as any)?.status);
          return st !== 'in transit';
        })
      : targetIndexes;
    if (!targetIndexes.length) {
      message.info('No editable packages found (created packages are locked)');
      return;
    }
    editableIndexes.forEach((idx) => {
      const row = current[idx];
      if (!row) return;
      const hasShipmentLink = Boolean(String((row as any).shipment_id || '').trim());
      fields.forEach((f) => {
        // Forever linkage: if shipment is assigned, transport_type is derived from shipment.
        if (hasShipmentLink && f === 'transport_type') return;
        const nextVal = (payload as any)[f];
        if (nextVal === undefined) return;
        const curVal = (row as any)[f];
        const hasCur = curVal !== null && curVal !== undefined && (typeof curVal !== 'string' || String(curVal).trim() !== '');
        const differs = String(curVal ?? '') !== String(nextVal ?? '');
        if (hasCur && differs) {
          willOverride.push({ idx, field: String(f), from: curVal, to: nextVal });
        }
      });
    });

    const doApply = () => {
      const next = [...current];
      editableIndexes.forEach((idx) => {
        const row = next[idx];
        if (!row) return;
        const hasShipmentLink = Boolean(String((row as any).shipment_id || '').trim());
        const patch: any = { ...(payload as any) };
        if (hasShipmentLink) {
          delete patch.transport_type;
        }
        next[idx] = { ...row, ...patch };
      });
      form.setFieldValue('packages', next);
      setTabTick((t) => t + 1);
      message.success(`Applied common fields to ${editableIndexes.length} package(s)`);
    };

    if (!willOverride.length) {
      doApply();
      return;
    }

    Modal.confirm({
      title: 'Override existing values?',
      content: `This will override existing values in ${new Set(willOverride.map((x) => x.idx)).size} package(s).`,
      okText: 'Override and apply',
      cancelText: 'Cancel',
      onOk: doApply,
    });
  }, [bulkForm, createdPackageIdSet, form, packageTabKeys]);

  const handlePlTemplateFile = React.useCallback(
    async (file: File) => {
      setPlLoading(true);
      try {
        const { rows } = await parsePlAttachment(file);
        const built = buildPackagesFromParsedRows(rows);
        if (!built.length) {
          message.error('PL Attachment template: no package items detected');
          return;
        }

        setPlFileName(file.name);

        const current = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
        const existingIdSet = new Set(
          (current || [])
            .map((p) => String(p?.package_id || '').trim())
            .filter((pid) => pid)
        );
        const overlappingIds = Array.from(
          new Set(
            (built || [])
              .map((b) => String(b.package_id || '').trim())
              .filter((pid) => pid && existingIdSet.has(pid))
          )
        );
        const conflictChoice = overlappingIds.length ? await askPlConflictChoice(overlappingIds) : 'merge';
        if (conflictChoice === 'cancel') {
          message.info('Import cancelled');
          return;
        }
        const nextPackages = [...current];
        const idToIndex = new Map<string, number>();
        nextPackages.forEach((p, idx) => {
          const pid = String(p?.package_id || '').trim();
          if (pid) idToIndex.set(pid, idx);
        });

        const defaultDraft: Omit<PackageHeaderDraft, 'package_id'> = {
          po_number: null,
          shipment_id: null,
          previous_shipment_id: null,
          transport_type: 'tbd',
          package_type: 'tbd' as PackageType,
          status: 'pending',
          net_weight_kg: null,
          gross_weight_kg: null,
          height_cm: null,
          width_cm: null,
          length_cm: null,
          final_destination: null,
          remarks: null,
        };

        let firstIdx: number | null = null;
        built.forEach((b) => {
          const pid = String(b.package_id || '').trim();
          if (!pid) return;

          let idx = idToIndex.get(pid);
          if (idx === undefined) {
            idx = nextPackages.length;
            nextPackages.push({
              ...defaultDraft,
              package_id: pid,
              po_number: (b as any).po_number ?? null,
              package_type: ((b as any).package_type ?? 'tbd') as PackageType,
              net_weight_kg: (b as any).net_weight_kg ?? null,
              gross_weight_kg: (b as any).gross_weight_kg ?? null,
              length_cm: (b as any).length_cm ?? null,
              width_cm: (b as any).width_cm ?? null,
              height_cm: (b as any).height_cm ?? null,
            });
            idToIndex.set(pid, idx);
          } else {
            const prev = nextPackages[idx] as PackageHeaderDraft;
            nextPackages[idx] = {
              ...prev,
              po_number: prev.po_number ?? (b as any).po_number ?? null,
              package_type: (prev.package_type && prev.package_type !== 'tbd' ? prev.package_type : (b as any).package_type ?? prev.package_type) as PackageType,
              net_weight_kg: prev.net_weight_kg ?? (b as any).net_weight_kg ?? null,
              gross_weight_kg: prev.gross_weight_kg ?? (b as any).gross_weight_kg ?? null,
              length_cm: prev.length_cm ?? (b as any).length_cm ?? null,
              width_cm: prev.width_cm ?? (b as any).width_cm ?? null,
              height_cm: prev.height_cm ?? (b as any).height_cm ?? null,
            };
          }

          if (firstIdx === null) firstIdx = idx;
        });

        form.setFieldValue('packages', nextPackages);
        setPackageTabKeys(Array.from({ length: nextPackages.length }, (_, i) => i));
        setTabTick((t) => t + 1);

        setItemsByIndex((prevAll) => {
          const next = { ...(prevAll || {}) };
          built.forEach((b: any) => {
            const pid = String(b.package_id || '').trim();
            const idx = idToIndex.get(pid);
            if (idx === undefined) return;
            const prev = next[idx] || [];
            const isOverlap = pid ? existingIdSet.has(pid) : false;
            const shouldReplace = conflictChoice === 'replace' && isOverlap;
            const baseItemId = shouldReplace ? 1 : getNextItemId(prev);
            const mapped = buildItemsFromPlRows(b.rows, baseItemId);
            next[idx] = shouldReplace ? mapped : [...prev, ...mapped];
          });
          return next;
        });

        if (firstIdx !== null) setActivePackageIndex(firstIdx);
        setTabTick((t) => t + 1);
        if (built.length > 1) {
          bulkForm.resetFields();
          setActiveTabKey('total');
        } else if (firstIdx !== null) {
          setActiveTabKey(String(firstIdx));
        }
        if (overlappingIds.length) {
          message.success(`PL imported: ${built.length} package(s). Overlaps: ${overlappingIds.length} (${conflictChoice}).`);
        } else {
          message.success(`PL imported: ${built.length} package(s)`);
        }
      } catch (e: any) {
        message.error(e?.message || 'Failed to parse PL Attachment template');
      } finally {
        setPlLoading(false);
        if (plInputRef.current) plInputRef.current.value = '';
      }
    },
    [askPlConflictChoice, bulkForm, form]
  );

  const itemsGrandTotal = React.useMemo(() => {
    const sum = (activeItems || []).reduce((acc, r) => {
      const up = typeof r.unit_price === 'number' ? r.unit_price : Number(String(r.unit_price ?? 0));
      const qty = typeof r.quantity === 'number' ? r.quantity : Number(String(r.quantity ?? 0));
      const t = up * qty;
      return acc + (Number.isFinite(t) ? t : 0);
    }, 0);
    return Number.isFinite(sum) ? sum : 0;
  }, [activeItems]);

  const handleItemsExcelLoaded = React.useCallback(
    (args: { rows: any[] }) => {
      const rawRows = Array.isArray(args?.rows) ? args.rows : [];
      if (!rawRows.length) {
        message.warning('No rows found in file');
        return;
      }
      const pkgId = String(form.getFieldValue(['packages', activePackageIndex, 'package_id']) || '').trim();
      if (!pkgId) {
        message.error('Please set Package ID first');
        return;
      }

      setItemsByIndex((prevAll) => {
        const prev = prevAll?.[activePackageIndex] || [];
        let nextItemId = getNextItemId(prev);

        const mapped: PackageItemDraft[] = [];
        (rawRows || []).forEach((r) => {
          const candidate: PackageItemDraft = {
            key: `import-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            item_id: toNumberOrNull((r as any).item_id),
            parent_item_code:
              (r as any).parent_item_code !== undefined && (r as any).parent_item_code !== null && String((r as any).parent_item_code).trim() !== ''
                ? String((r as any).parent_item_code)
                : null,
            itemcode: (r as any).itemcode !== undefined && (r as any).itemcode !== null && String((r as any).itemcode).trim() !== '' ? String((r as any).itemcode) : null,
            size_text: (r as any).size_text !== undefined && (r as any).size_text !== null && String((r as any).size_text).trim() !== '' ? String((r as any).size_text) : null,
            short_description:
              (r as any).short_description !== undefined && (r as any).short_description !== null && String((r as any).short_description).trim() !== ''
                ? String((r as any).short_description)
                : null,
            long_description:
              (r as any).long_description !== undefined && (r as any).long_description !== null && String((r as any).long_description).trim() !== ''
                ? String((r as any).long_description)
                : null,
            notes: (r as any).notes !== undefined && (r as any).notes !== null && String((r as any).notes).trim() !== '' ? String((r as any).notes) : null,
            hs_code: (r as any).hs_code !== undefined && (r as any).hs_code !== null && String((r as any).hs_code).trim() !== '' ? String((r as any).hs_code) : null,
            country_of_origin:
              (r as any).country_of_origin !== undefined && (r as any).country_of_origin !== null && String((r as any).country_of_origin).trim() !== ''
                ? String((r as any).country_of_origin)
                : (r as any).contry_of_origin !== undefined && (r as any).contry_of_origin !== null && String((r as any).contry_of_origin).trim() !== ''
                  ? String((r as any).contry_of_origin)
                  : null,
            unit: (r as any).unit !== undefined && (r as any).unit !== null && String((r as any).unit).trim() !== '' ? String((r as any).unit) : null,
            unit_price: toNumberOrNull((r as any).unit_price),
            currency:
              (r as any).currency !== undefined && (r as any).currency !== null && String((r as any).currency).trim() !== ''
                ? String((r as any).currency).toUpperCase().slice(0, 3)
                : 'USD',
            quantity: toNumberOrNull((r as any).quantity),
          };

          const hasAny =
            candidate.parent_item_code !== null ||
            candidate.itemcode !== null ||
            candidate.size_text !== null ||
            candidate.short_description !== null ||
            candidate.long_description !== null ||
            candidate.notes !== null ||
            candidate.hs_code !== null ||
            candidate.country_of_origin !== null ||
            candidate.unit !== null ||
            candidate.unit_price !== null ||
            candidate.quantity !== null;

          if (!hasAny) return;

          if (candidate.item_id === null) {
            candidate.item_id = nextItemId;
            nextItemId += 1;
          }

          mapped.push(candidate);
        });

        if (!mapped.length) {
          message.warning('No usable rows found (empty rows skipped)');
          return prevAll;
        }

        message.success(`${mapped.length} item(s) added to list`);
        return { ...(prevAll || {}), [activePackageIndex]: [...prev, ...mapped] };
      });
    },
    [activePackageIndex, form, toNumberOrNull]
  );

  const { data: shipmentsData, isLoading: shipmentsLoading } = useGetMatLogisticShipments();
  const allShipmentOptions = React.useMemo(() => {
    const rows = (shipmentsData?.data ?? shipmentsData ?? []) as Array<any>;
    return rows
      .filter((r) => r?.shipment_id)
      .map((r) => ({
        label: `${String(r.shipment_id)}`,
        value: String(r.shipment_id),
        transport_type: r?.transport_type ? String(r.transport_type) : undefined,
        arrival_status: r?.arrival_status ? String(r.arrival_status) : undefined,
        status: r?.status ? String(r.status) : undefined,
      }));
  }, [shipmentsData]);

  const shipmentOptions = React.useMemo(() => {
    const norm = (v: any) => String(v ?? '').trim().toLowerCase();
    const isCancelled = (opt: any) => norm(opt?.status) === 'cancelled';
    const isFrozen = (opt: any) => {
      const s = norm(opt?.arrival_status);
      return s === 'in transit' || s === 'arrived';
    };

    const base = (allShipmentOptions || []).filter((opt) => !isFrozen(opt) && !isCancelled(opt));

    // Ensure existing assigned shipment (even if frozen) can still render in Select.
    const currentShipmentId = String(form.getFieldValue(['packages', activePackageIndex, 'shipment_id']) || '').trim();
    if (currentShipmentId && !base.some((x) => x.value === currentShipmentId)) {
      const existing = (allShipmentOptions || []).find((x) => x.value === currentShipmentId);
      base.push(
        existing || {
          label: currentShipmentId,
          value: currentShipmentId,
          transport_type: undefined,
          arrival_status: undefined,
          status: undefined,
        }
      );
    }

    return base;
  }, [activePackageIndex, allShipmentOptions, form]);

  const onShipmentSelect = React.useCallback(
    (shipmentId: string) => {
      const s = shipmentOptions.find((x) => x.value === shipmentId);
      if (s?.transport_type) {
        // Keep synced forever: shipment assignment determines package transport_type.
        form.setFieldValue(['packages', activePackageIndex, 'transport_type'], s.transport_type as TransportType);
      }
    },
    [activePackageIndex, form, shipmentOptions]
  );

  const onShipmentChange = React.useCallback(
    (nextShipmentId: string | null) => {
      const sid = nextShipmentId ? String(nextShipmentId) : '';
      if (!sid) {
        // Shipment cleared: release lock and fall back to default.
        form.setFieldValue(['packages', activePackageIndex, 'transport_type'], 'tbd' as TransportType);
        return;
      }
      onShipmentSelect(sid);
    },
    [activePackageIndex, form, onShipmentSelect]
  );

  const isPackageCreated = React.useCallback(
    (idx: number) => {
      if (isEditMode) return false;
      const pid = String(form.getFieldValue(['packages', idx, 'package_id']) || '').trim();
      if (!pid) return false;
      return createdPackageIdSet.has(pid);
    },
    [createdPackageIdSet, form, isEditMode]
  );

  const getCreateCandidateIndexes = React.useCallback(() => {
    const pkgs = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
    const out: number[] = [];
    (pkgs || []).forEach((p, idx) => {
      const pid = String(p?.package_id || '').trim();
      if (!pid) return;
      if (createdPackageIdSet.has(pid)) return;
      out.push(idx);
    });
    return out;
  }, [createdPackageIdSet, form]);

  const validatePackageHeader = React.useCallback(
    async (idx: number) => {
      const paths = [
        ['packages', idx, 'package_id'],
        ['packages', idx, 'transport_type'],
        ['packages', idx, 'package_type'],
        ['packages', idx, 'status'],
      ] as any[];
      await form.validateFields(paths);
    },
    [form]
  );

  const buildHeaderPayload = React.useCallback(
    (idx: number) => {
      const p = (form.getFieldValue(['packages', idx]) || {}) as PackageHeaderDraft;
      const pid = String(p.package_id || '').trim();
      return {
        po_number: p.po_number ? String(p.po_number) : null,
        package_id: pid,
        shipment_id: p.shipment_id ? String(p.shipment_id) : null,
        previous_shipment_id: p.previous_shipment_id ? String(p.previous_shipment_id) : null,
        transport_type: String(p.transport_type),
        package_type: String(p.package_type),
        status: String(p.status),
        upload_path: p.upload_path ? String(p.upload_path) : null,
        net_weight_kg: toNumberOrNull(p.net_weight_kg),
        gross_weight_kg: toNumberOrNull(p.gross_weight_kg),
        height_cm: toNumberOrNull(p.height_cm),
        width_cm: toNumberOrNull(p.width_cm),
        length_cm: toNumberOrNull(p.length_cm),
        final_destination: p.final_destination ?? null,
        remarks: p.remarks ?? null,
      };
    },
    [form, toNumberOrNull]
  );

  const buildItemsPayload = React.useCallback(
    (idx: number, pid: string) => {
      const rows = itemsByIndex?.[idx] || [];
      return (rows || [])
        .map((r) => ({
          package_id: pid,
          item_id: toNumberOrNull(r.item_id),
          parent_item_code: r.parent_item_code ? String(r.parent_item_code) : null,
          itemcode: r.itemcode ? String(r.itemcode) : null,
          size_text: r.size_text ? String(r.size_text) : null,
          short_description: r.short_description ? String(r.short_description) : null,
          long_description: r.long_description ? String(r.long_description) : null,
          notes: r.notes ? String(r.notes) : null,
          hs_code: r.hs_code ? String(r.hs_code) : null,
          country_of_origin: r.country_of_origin ? String(r.country_of_origin) : null,
          unit: r.unit ? String(r.unit) : null,
          unit_price: toNumberOrNull(r.unit_price),
          currency: (r.currency ? String(r.currency) : 'USD').toUpperCase().slice(0, 3),
          quantity: toNumberOrNull(r.quantity),
        }))
        .filter((r) =>
          r.item_id !== null ||
          r.parent_item_code !== null ||
          r.itemcode !== null ||
          r.size_text !== null ||
          r.short_description !== null ||
          r.long_description !== null ||
          r.notes !== null ||
          r.hs_code !== null ||
          r.country_of_origin !== null ||
          r.unit !== null ||
          r.unit_price !== null ||
          r.quantity !== null
        );
    },
    [itemsByIndex, toNumberOrNull]
  );

  const createOne = React.useCallback(
    async (idx: number) => {
      await validatePackageHeader(idx);
      const headerPayload: any = buildHeaderPayload(idx);
      const pid = String(headerPayload.package_id || '').trim();
      if (!pid) throw new Error('Package ID is empty');

      const itemsPayload = buildItemsPayload(idx, pid);

      const fu = fileUploadRefsByIndex.current?.[idx] || null;
      if (fu && fu.hasFilesToUpload()) {
        const up = await fu.finalizeUpload(pid);
        if (!up.success) {
          throw new Error(up.error || 'File upload failed');
        }
        headerPayload.upload_path = up.folderPath || null;
      }

      await insertHeaderWithItems({
        header_table: 'mat_logistic_packages',
        header_data: headerPayload,
        items_table: 'mat_logistic_package_items',
        items: itemsPayload,
      });

      if (headerPayload.upload_path !== undefined) {
        setUploadPathByIndex((prev) => ({ ...(prev || {}), [idx]: String(headerPayload.upload_path || '') }));
      }

      setCreatedPackageIds((prev) => {
        const s = new Set((prev || []).map((x) => String(x || '').trim()));
        s.add(pid);
        return Array.from(s);
      });

      return pid;
    },
    [buildHeaderPayload, buildItemsPayload, validatePackageHeader]
  );

  const onCreateCurrent = React.useCallback(async () => {
    if (activeTabKey === 'total') return;
    const idx = activePackageIndex;
    const pid = String(form.getFieldValue(['packages', idx, 'package_id']) || '').trim();
    if (!pid) {
      message.error('Please enter Package ID');
      return;
    }
    if (createdPackageIdSet.has(pid)) {
      message.info(`Already created: ${pid}`);
      return;
    }
    setSubmitting(true);
    try {
      await createOne(idx);
      message.success(`Created package(s): ${pid}. Created packages are locked; other packages remain editable.`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to create package');
    } finally {
      setSubmitting(false);
    }
  }, [activePackageIndex, activeTabKey, createOne, createdPackageIdSet, form]);

  const onSaveEdit = React.useCallback(async () => {
    if (!isEditMode) return;
    if (activeTabKey === 'total') return;

    const idx = activePackageIndex;
    const pid = String(form.getFieldValue(['packages', idx, 'package_id']) || '').trim();
    const hdrId = editHeaderIdByIndex[idx];

    if (!Number.isFinite(hdrId)) {
      message.error('Missing header id for this package (cannot save)');
      return;
    }
    if (!pid) {
      message.error('Please enter Package ID');
      return;
    }

    setSubmitting(true);
    try {
      await validatePackageHeader(idx);

      const headerPayload: any = buildHeaderPayload(idx);
      const fu = fileUploadRefsByIndex.current?.[idx] || null;
      if (fu && fu.hasFilesToUpload()) {
        const up = await fu.finalizeUpload(pid);
        if (!up.success) {
          message.error(up.error || 'File upload failed. Changes not saved.');
          return;
        }
        headerPayload.upload_path = up.folderPath || null;
      }
      const itemsPayload = buildItemsPayload(idx, pid);

      await deleteData('mat_logistic_package_items', 'package_id', pid);

      await insertHeaderWithItems({
        header_table: 'mat_logistic_packages',
        header_id: hdrId,
        header_data: headerPayload,
        items_table: 'mat_logistic_package_items',
        items: itemsPayload,
      });

      if (headerPayload.upload_path !== undefined) {
        setUploadPathByIndex((prev) => ({ ...(prev || {}), [idx]: String(headerPayload.upload_path || '') }));
      }

      message.success(`Saved: ${pid}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to save package');
    } finally {
      setSubmitting(false);
    }
  }, [activePackageIndex, activeTabKey, buildHeaderPayload, buildItemsPayload, editHeaderIdByIndex, form, isEditMode, validatePackageHeader]);

  const onSaveAllEdit = React.useCallback(async () => {
    if (!isEditMode) return;
    const pkgsAll = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
    const idxs = Array.from({ length: pkgsAll.length }, (_, i) => i).filter((i) => {
      const pid = String(pkgsAll?.[i]?.package_id || '').trim();
      const hid = editHeaderIdByIndex[i];
      return Boolean(pid) && Number.isFinite(hid);
    });
    if (!idxs.length) {
      message.warning('No packages to save');
      return;
    }

    try {
      for (const idx of idxs) {
        await validatePackageHeader(idx);
      }
    } catch (e: any) {
      if (e?.errorFields) {
        const first = idxs[0];
        setActivePackageIndex(first);
        setActiveTabKey(String(first));
      }
      return;
    }

    Modal.confirm({
      title: 'Save all packages?',
      content: `This will save ${idxs.length} package(s). Item lists will be replaced for each package.`,
      okText: 'Save All',
      cancelText: 'Cancel',
      onOk: async () => {
        setSubmitting(true);
        setCreateAllState({ running: true, total: idxs.length, done: 0, current: null });
        const successes: string[] = [];
        const failures: Array<{ pid: string; message: string }> = [];
        for (const idx of idxs) {
          const pid = String(pkgsAll?.[idx]?.package_id || '').trim();
          const hdrId = editHeaderIdByIndex[idx];
          setCreateAllState((s) => ({ ...s, current: pid || null }));
          try {
            const headerPayload = buildHeaderPayload(idx);
            const itemsPayload = buildItemsPayload(idx, pid);
            await deleteData('mat_logistic_package_items', 'package_id', pid);
            await insertHeaderWithItems({
              header_table: 'mat_logistic_packages',
              header_id: hdrId,
              header_data: headerPayload,
              items_table: 'mat_logistic_package_items',
              items: itemsPayload,
            });
            successes.push(pid);
          } catch (e: any) {
            failures.push({ pid, message: e?.message || 'Unknown error' });
          } finally {
            setCreateAllState((s) => ({ ...s, done: Math.min(s.total, s.done + 1) }));
          }
        }
        setCreateAllState({ running: false, total: 0, done: 0, current: null });
        setSubmitting(false);

        if (successes.length) {
          message.success(`Saved: ${successes.join(', ')}`);
        }
        if (failures.length) {
          Modal.warning({
            title: 'Save All completed with errors',
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>{`Saved ${successes.length}. Failed ${failures.length}.`}</div>
                <div style={{ maxHeight: 240, overflow: 'auto' }}>
                  {failures.map((f) => (
                    <div key={f.pid} style={{ marginBottom: 6 }}>
                      <b>{f.pid || '(no id)'}</b>: {f.message}
                    </div>
                  ))}
                </div>
              </div>
            ),
          });
        }
      },
    });
  }, [buildHeaderPayload, buildItemsPayload, editHeaderIdByIndex, form, isEditMode, validatePackageHeader]);

  const onCreateAll = React.useCallback(async () => {
    const pkgsAll = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
    const emptyIdIdxs = (pkgsAll || [])
      .map((p, idx) => ({ idx, pid: String(p?.package_id || '').trim() }))
      .filter((x) => !x.pid)
      .map((x) => x.idx);

    const candidateIdxs = getCreateCandidateIndexes();
    const candidateIds = candidateIdxs.map((i) => String(pkgsAll?.[i]?.package_id || '').trim()).filter(Boolean);
    const alreadyCreatedCount =
      (pkgsAll || []).filter((p) => {
        const pid = String(p?.package_id || '').trim();
        if (!pid) return false;
        return createdPackageIdSet.has(pid);
      }).length ?? 0;

    if (!candidateIdxs.length) {
      const parts = [
        emptyIdIdxs.length ? `${emptyIdIdxs.length} empty Package ID` : null,
        alreadyCreatedCount ? `${alreadyCreatedCount} already created` : null,
      ].filter(Boolean);
      message.warning(`No packages to create${parts.length ? ` (skipped: ${parts.join(', ')})` : ''}`);
      return;
    }

    try {
      for (const idx of candidateIdxs) {
        await validatePackageHeader(idx);
      }
    } catch (e: any) {
      if (e?.errorFields) {
        const first = candidateIdxs[0];
        setActivePackageIndex(first);
        setActiveTabKey(String(first));
      }
      return;
    }

    const ids = candidateIds;
    const itemCount = candidateIdxs.reduce((acc, i) => acc + ((itemsByIndex?.[i] || []).length || 0), 0);

    Modal.confirm({
      title: 'Create all packages?',
      content: `This will create ${ids.length} package(s) with ${itemCount} item row(s). Skipped: ${
        emptyIdIdxs.length ? `${emptyIdIdxs.length} empty Package ID` : '0 empty Package ID'
      }, ${alreadyCreatedCount} already created.`,
      okText: 'Create All',
      cancelText: 'Cancel',
      onOk: async () => {
        setSubmitting(true);
        setCreateAllState({ running: true, total: ids.length, done: 0, current: null });
        const successes: string[] = [];
        const failures: Array<{ pid: string; message: string }> = [];

        for (let i = 0; i < candidateIdxs.length; i += 1) {
          const idx = candidateIdxs[i];
          const pid = String(pkgsAll?.[idx]?.package_id || '').trim();
          setCreateAllState((s) => ({ ...s, current: pid || null }));
          try {
            const createdPid = await createOne(idx);
            successes.push(createdPid);
            setCreateAllState((s) => ({ ...s, done: Math.min(s.total, s.done + 1) }));
          } catch (e: any) {
            failures.push({ pid, message: e?.message || 'Unknown error' });
            setCreateAllState((s) => ({ ...s, done: Math.min(s.total, s.done + 1) }));
            continue;
          }
        }

        setCreateAllState({ running: false, total: 0, done: 0, current: null });
        setSubmitting(false);

        if (successes.length) {
          message.success(`Created package(s): ${successes.join(', ')}. Created packages are locked; other packages remain editable.`);
        }
        if (emptyIdIdxs.length) {
          message.info(`Skipped ${emptyIdIdxs.length} package(s) with empty Package ID.`);
        }

        if (!failures.length) {
          Modal.info({
            title: 'Create All completed',
            content: `Created ${successes.length} package(s).`,
          });
          return;
        }

        Modal.warning({
          title: 'Create All completed with errors',
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>{`Created ${successes.length} package(s). Failed ${failures.length}.`}</div>
              <div style={{ maxHeight: 240, overflow: 'auto' }}>
                {failures.map((f) => (
                  <div key={f.pid} style={{ marginBottom: 6 }}>
                    <b>{f.pid || '(no id)'}</b>: {f.message}
                  </div>
                ))}
              </div>
            </div>
          ),
        });
      },
    });
  }, [createOne, createdPackageIdSet, form, getCreateCandidateIndexes, itemsByIndex, validatePackageHeader]);

  const addItemRow = React.useCallback(() => {
    setEditingItemKey(null);
    setEditingPackageIndex(activePackageIndex);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      item_id: getNextItemId(itemsByIndex?.[activePackageIndex] || []),
      parent_item_code: null,
      itemcode: null,
      size_text: null,
      short_description: null,
      long_description: null,
      notes: null,
      hs_code: null,
      country_of_origin: null,
      unit: null,
      unit_price: null,
      currency: 'USD',
      quantity: null,
    });
    setDrawerOpen(true);
  }, [activePackageIndex, itemForm, itemsByIndex]);

  const editItemRow = React.useCallback(
    (row: PackageItemDraft) => {
      setEditingItemKey(row.key);
      setEditingPackageIndex(activePackageIndex);
      itemForm.resetFields();
      itemForm.setFieldsValue({
        item_id: row.item_id ?? null,
        parent_item_code: row.parent_item_code ?? null,
        itemcode: row.itemcode ?? null,
        size_text: row.size_text ?? null,
        short_description: row.short_description ?? null,
        long_description: row.long_description ?? null,
        notes: row.notes ?? null,
        hs_code: row.hs_code ?? null,
        country_of_origin: row.country_of_origin ?? null,
        unit: row.unit ?? null,
        unit_price: row.unit_price ?? null,
        currency: (row.currency ?? 'USD') as any,
        quantity: row.quantity ?? null,
      });
      setDrawerOpen(true);
    },
    [activePackageIndex, itemForm]
  );

  const removeItemRow = React.useCallback(
    (key: string) => {
      setItemsByIndex((prevAll) => {
        const prev = prevAll?.[activePackageIndex] || [];
        return { ...(prevAll || {}), [activePackageIndex]: prev.filter((r) => r.key !== key) };
      });
    },
    [activePackageIndex]
  );

  const saveDrawerItem = React.useCallback(async () => {
    try {
      const v = await itemForm.validateFields();
      const normalizedBase = {
        ...v,
        item_id: toNumberOrNull((v as any).item_id),
        unit_price: toNumberOrNull((v as any).unit_price),
        currency: (v as any).currency ? String((v as any).currency).toUpperCase().slice(0, 3) : 'USD',
        quantity: toNumberOrNull((v as any).quantity),
      } as PackageItemFormValues;

      const targetIdx = editingPackageIndex ?? activePackageIndex;
      const currentItems = itemsByIndex?.[targetIdx] || [];

      const normalized =
        !editingItemKey && normalizedBase.item_id === null
          ? ({
              ...normalizedBase,
              item_id: getNextItemId(currentItems),
            } as PackageItemFormValues)
          : normalizedBase;
      if (editingItemKey) {
        setItemsByIndex((prevAll) => {
          const prev = prevAll?.[targetIdx] || [];
          return { ...(prevAll || {}), [targetIdx]: prev.map((r) => (r.key === editingItemKey ? { ...r, ...normalized } : r)) };
        });
      } else {
        setItemsByIndex((prevAll) => {
          const prev = prevAll?.[targetIdx] || [];
          return {
            ...(prevAll || {}),
            [targetIdx]: [
              ...prev,
              {
                key: `new_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                ...normalized,
              },
            ],
          };
        });
      }
      setDrawerOpen(false);
      setEditingItemKey(null);
      setEditingPackageIndex(null);
      itemForm.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Invalid item');
    }
  }, [activePackageIndex, editingItemKey, editingPackageIndex, itemForm, itemsByIndex, toNumberOrNull]);

  const itemStyle: React.CSSProperties = { marginBottom: 8 };
  const activeDelivered = React.useMemo(() => {
    if (activeTabKey === 'total') return false;
    const s = String(form.getFieldValue(['packages', activePackageIndex, 'status']) || '')
      .trim()
      .toLowerCase();
    return s === 'delivered';
  }, [activePackageIndex, activeTabKey, form, tabTick]);
  const activeInTransit = React.useMemo(() => {
    if (activeTabKey === 'total') return false;
    const s = String(form.getFieldValue(['packages', activePackageIndex, 'status']) || '')
      .trim()
      .toLowerCase();
    return s === 'in transit';
  }, [activePackageIndex, activeTabKey, form, tabTick]);
  const activeLocked = activeTabKey !== 'total' && (isPackageCreated(activePackageIndex) || activeDelivered);
  const activeShipmentId = React.useMemo(() => {
    return String(form.getFieldValue(['packages', activePackageIndex, 'shipment_id']) || '').trim();
  }, [activePackageIndex, form, tabTick]);
  const activeTransportLockedByShipment = Boolean(activeShipmentId);
  const packageContentsTitle = React.useMemo(() => {
    if (activeTabKey === 'total') return 'Package Contents — Total';
    const pid = String(form.getFieldValue(['packages', activePackageIndex, 'package_id']) || '').trim();
    const label = pid ? pid : `Package ${activePackageIndex + 1}`;
    return `Package Contents — ${label}`;
  }, [activePackageIndex, activeTabKey, form, tabTick]);

  if (isEditMode && initialLoading) {
    return (
      <Card style={{ margin: 8 }} loading>
        <div style={{ height: 240 }} />
      </Card>
    );
  }

  return (
    <Card style={{ margin: 8 }}>
      <style>{`
        .matlog-package-form .ant-input-number-input {
          text-align: right;
        }
        .matlog-package-tabs .ant-tabs-nav-wrap {
          overflow-x: auto !important;
          overflow-y: hidden !important;
          scrollbar-width: thin;
        }
        .matlog-package-tabs .ant-tabs-nav-more {
          display: none !important;
        }
        .matlog-package-tabs .ant-tabs-nav-list {
          transform: none !important;
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: 12 }}>
        <Title level={3} style={{ margin: 0 }}>
          {isEditMode ? 'Edit Package' : 'Create Package'}
        </Title>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Space>
            <Button
              onClick={() => {
                if (isEditMode) {
                  if (!normalizedEditIds.length) {
                    message.warning('No package ids to reload');
                    return;
                  }
                  Modal.confirm({
                    title: 'Reset changes?',
                    content: 'This will discard unsaved changes and reload the package(s) from server.',
                    okText: 'Reload',
                    cancelText: 'Cancel',
                    onOk: async () => {
                      await loadEditPackages(normalizedEditIds);
                    },
                  });
                  return;
                }

                form.resetFields();
                setCreatedPackageIds([]);
                setItemsByIndex({});
                itemForm.resetFields();
                setDrawerOpen(false);
                setEditingItemKey(null);
                setEditingPackageIndex(null);
                setPlFileName('');
                setActivePackageIndex(0);
                setActiveTabKey('0');
                setPackageTabKeys([0]);
                setTabTick((t) => t + 1);
              }}
              disabled={submitting || createAllState.running}
            >
              Reset
            </Button>
            <Button
              action={isEditMode ? 'edit' : 'add'}
              type="primary"
              onClick={isEditMode ? onSaveEdit : onCreateCurrent}
              loading={submitting && !createAllState.running}
              disabled={
                submitting ||
                createAllState.running ||
                activeTabKey === 'total' ||
                (!isEditMode && isPackageCreated(activePackageIndex)) ||
                String(form.getFieldValue(['packages', activePackageIndex, 'package_id']) || '').trim() === '' ||
                initialLoading
              }
            >
              {isEditMode ? 'Save' : 'Create'}
            </Button>
            {isEditMode ? (
              packageTabKeys.length > 1 ? (
                <Button action="edit" onClick={onSaveAllEdit} disabled={submitting || createAllState.running || initialLoading}>
                  Save All
                </Button>
              ) : null
            ) : (
              <Button action="add" onClick={onCreateAll} disabled={submitting || createAllState.running || getCreateCandidateIndexes().length === 0}>
                Create All
              </Button>
            )}
            {createAllState.running ? (
              <span style={{ fontSize: 12, opacity: 0.75 }}>
                Creating {createAllState.done}/{createAllState.total}
                {createAllState.current ? ` (${createAllState.current})` : ''}
              </span>
            ) : null}
          </Space>
        </div>
      </div>

      <Tabs
        className="matlog-package-tabs"
        size="small"
        activeKey={activeTabKey}
        onChange={(k) => {
          setActiveTabKey(k);
          if (k !== 'total') {
            setActivePackageIndex(Number(k));
          }
        }}
        tabBarStyle={{ margin: '0 12px 8px 12px' }}
        items={[
          ...(showTotalTab ? [{ key: 'total', label: 'Total' }] : []),
          ...(packageTabKeys || []).map((idx) => {
            const p = (packages || [])[idx];
            const pid = String(form.getFieldValue(['packages', idx, 'package_id']) || p?.package_id || '').trim();
            return {
              key: String(idx),
              label: pid ? pid : `Package ${idx + 1}`,
            };
          }),
        ]}
        tabBarExtraContent={
          isEditMode ? null : (
            <Space size={6}>
              <Button
                size="small"
                disabled={submitting || createAllState.running}
                onClick={() => {
                  const current = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
                  const next = [
                    ...current,
                    {
                      package_id: '',
                      po_number: null,
                      shipment_id: null,
                      previous_shipment_id: null,
                      transport_type: 'tbd' as TransportType,
                      package_type: 'tbd' as PackageType,
                      status: 'pending' as PackageStatus,
                      net_weight_kg: null,
                      gross_weight_kg: null,
                      height_cm: null,
                      width_cm: null,
                      length_cm: null,
                      final_destination: null,
                      remarks: null,
                    } as PackageHeaderDraft,
                  ];
                  form.setFieldValue('packages', next);
                  setPackageTabKeys(Array.from({ length: next.length }, (_, i) => i));
                  setActivePackageIndex(next.length - 1);
                  setActiveTabKey(String(next.length - 1));
                  setTabTick((t) => t + 1);
                }}
              >
                Add
              </Button>

              <Button
                size="small"
                danger
                disabled={submitting || createAllState.running || activeTabKey === 'total' || isPackageCreated(activePackageIndex)}
                onClick={() => {
                  const current = (form.getFieldValue('packages') || []) as PackageHeaderDraft[];
                  const removingIdx = activePackageIndex;
                  if (!Array.isArray(current) || !current.length) return;

                  if (current.length <= 1) {
                    const next = [
                      {
                        package_id: '',
                        po_number: null,
                        shipment_id: null,
                        previous_shipment_id: null,
                        transport_type: 'tbd' as TransportType,
                        package_type: 'tbd' as PackageType,
                        status: 'pending' as PackageStatus,
                        net_weight_kg: null,
                        gross_weight_kg: null,
                        height_cm: null,
                        width_cm: null,
                        length_cm: null,
                        final_destination: null,
                        remarks: null,
                      } as PackageHeaderDraft,
                    ];
                    form.setFieldValue('packages', next);
                    setItemsByIndex({ 0: [] });
                    setPackageTabKeys([0]);
                    setActivePackageIndex(0);
                    setActiveTabKey('0');
                    setTabTick((t) => t + 1);
                    return;
                  }

                  const next = current.filter((_, i) => i !== removingIdx);
                  form.setFieldValue('packages', next);
                  setPackageTabKeys(Array.from({ length: next.length }, (_, i) => i));

                  setItemsByIndex((prevAll) => {
                    const prev = prevAll || {};
                    const shifted: Record<number, PackageItemDraft[]> = {};
                    Object.keys(prev).forEach((k) => {
                      const idx = Number(k);
                      if (!Number.isFinite(idx)) return;
                      if (idx === removingIdx) return;
                      const newIdx = idx > removingIdx ? idx - 1 : idx;
                      shifted[newIdx] = prev[idx] || [];
                    });
                    return shifted;
                  });

                  const nextActive = Math.max(0, Math.min(removingIdx, next.length - 1));
                  setActivePackageIndex(nextActive);
                  setActiveTabKey(String(nextActive));
                  setTabTick((t) => t + 1);
                }}
              >
                Remove
              </Button>
            </Space>
          )
        }
      />

      <Row gutter={[12, 12]} wrap={false} align="top" style={{ width: '100%' }}>
        <Col flex="360px">
          {activeTabKey === 'total' ? (
            <div style={{ marginTop: 6 }}>
              <Form
                form={bulkForm}
                layout="horizontal"
                colon={false}
                labelAlign="left"
                labelCol={{ flex: '160px' }}
                wrapperCol={{ flex: 'auto' }}
                style={{ marginBottom: 12 }}
              >
                <Form.Item name="shipment_id" label="Shipment" style={itemStyle}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={shipmentsLoading}
                    options={shipmentOptions}
                    placeholder="Optional"
                    onSelect={(shipmentId: any) => {
                      const s = shipmentOptions.find((x) => x.value === String(shipmentId));
                      const current = bulkForm.getFieldValue('transport_type');
                      if (!current && s?.transport_type) {
                        bulkForm.setFieldValue('transport_type', s.transport_type as any);
                      }
                    }}
                  />
                </Form.Item>

                <Form.Item name="previous_shipment_id" label="Previous Shipment" style={itemStyle}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={shipmentsLoading}
                    options={allShipmentOptions}
                    placeholder="Optional"
                  />
                </Form.Item>

                <Form.Item name="transport_type" label="Transport" style={itemStyle}>
                  <Select allowClear options={transportTypeOptions.map((v) => ({ label: v, value: v }))} placeholder="Optional" />
                </Form.Item>

                <Form.Item name="status" label="Status" style={itemStyle}>
                  <Select allowClear options={statusOptions.map((v) => ({ label: v, value: v }))} placeholder="Optional" />
                </Form.Item>

                <Form.Item name="final_destination" label="Final Destination" style={itemStyle} normalize={toUpperOrSame}>
                  <Input placeholder="Optional" />
                </Form.Item>
                <Form.Item name="remarks" label="Remarks" style={itemStyle} normalize={toUpperOrSame}>
                  <Input placeholder="Optional" />
                </Form.Item>

                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => bulkForm.resetFields()}>Clear</Button>
                  <Button type="primary" onClick={applyCommonFieldsToAll} disabled={submitting || createAllState.running}>
                    Apply to All
                  </Button>
                </Space>
              </Form>

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220 }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>Total Freight Ton</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{totalAll.freightTon.toFixed(3)}</div>
                </div>
                <div style={{ minWidth: 220 }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>Total Price</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{totalAll.totalPrice.toFixed(3)}</div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <StandardTable<any>
                  size="small"
                  pagination={false as any}
                  rowKey={(r) => r.key}
                  dataSource={(packageTabKeys || []).map((idx) => {
                    const pid = String(form.getFieldValue(['packages', idx, 'package_id']) || '').trim() || `Package ${idx + 1}`;
                    const t = totalsByPackageIndex[idx] || { freightTon: 0, totalPrice: 0 };
                    return { key: String(idx), idx, package_id: pid, freight_ton: t.freightTon, total_price: t.totalPrice };
                  })}
                  columns={[
                    { title: 'Package', dataIndex: 'package_id', key: 'package_id', width: 140 },
                    {
                      title: 'Freight Ton',
                      dataIndex: 'freight_ton',
                      key: 'freight_ton',
                      width: 140,
                      align: 'right' as const,
                      className: 'ant-table-column-align-right',
                      render: (v: any) => {
                        const n = typeof v === 'number' ? v : Number(String(v));
                        return Number.isFinite(n) ? n.toFixed(3) : '';
                      },
                    },
                    {
                      title: 'Total Price',
                      dataIndex: 'total_price',
                      key: 'total_price',
                      width: 160,
                      align: 'right' as const,
                      className: 'ant-table-column-align-right',
                      render: (v: any) => {
                        const n = typeof v === 'number' ? v : Number(String(v));
                        return Number.isFinite(n) ? n.toFixed(3) : '';
                      },
                    },
                  ] as ColumnType<any>[]}
                />
              </div>
            </div>
          ) : (
            <Form
              form={form}
              className="matlog-package-form"
              layout="horizontal"
              colon={false}
              labelAlign="left"
              labelCol={{ flex: '160px' }}
              wrapperCol={{ flex: 'auto' }}
              style={{ width: '100%', maxWidth: 360, marginTop: 6 }}
              onValuesChange={() => {
                setTabTick((t) => t + 1);
              }}
            >
              <Form.Item
                name={['packages', activePackageIndex, 'package_id']}
                label="Package ID"
                rules={[{ required: true }]}
                style={itemStyle}
                normalize={toUpperOrSame}
              >
                <Input maxLength={30} disabled={isEditMode || submitting || createAllState.running || activeLocked} />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'po_number']} label="PO Number" style={itemStyle} normalize={toUpperOrSame}>
                <Input maxLength={30} disabled={submitting || createAllState.running || activeLocked} placeholder="Optional" />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'shipment_id']} label="Shipment" style={itemStyle}>
                {activeInTransit ? (
                  <Text strong type="warning">
                    {String(form.getFieldValue(['packages', activePackageIndex, 'shipment_id']) || '').trim() || '-'}
                  </Text>
                ) : (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={shipmentsLoading}
                    options={shipmentOptions}
                    placeholder="Optional"
                    onChange={onShipmentChange as any}
                    disabled={submitting || createAllState.running || activeLocked}
                  />
                )}
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'previous_shipment_id']} label="Previous Shipment" style={itemStyle}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={shipmentsLoading}
                  options={allShipmentOptions}
                  placeholder="Optional"
                  disabled={submitting || createAllState.running || activeLocked}
                />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'transport_type']} label="Transport" rules={[{ required: true }]} style={itemStyle}>
                {activeInTransit ? (
                  <Text strong type="warning">
                    {String(form.getFieldValue(['packages', activePackageIndex, 'transport_type']) || '').trim() || '-'}
                  </Text>
                ) : (
                  <Select
                    disabled={submitting || createAllState.running || activeLocked || activeTransportLockedByShipment}
                    options={transportTypeOptions.map((v) => ({ label: v, value: v }))}
                  />
                )}
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'package_type']} label="Package Type" rules={[{ required: true }]} style={itemStyle}>
                <Select
                  disabled={submitting || createAllState.running || activeLocked}
                  showSearch
                  optionFilterProp="label"
                  options={packageTypeOptions.map((v) => ({ label: v, value: v }))}
                />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'status']} label="Status" rules={[{ required: true }]} style={itemStyle}>
                {activeInTransit ? (
                  <Text strong type="warning">
                    {String(form.getFieldValue(['packages', activePackageIndex, 'status']) || '').trim() || '-'}
                  </Text>
                ) : (
                  <Select
                    disabled={submitting || createAllState.running || activeLocked}
                    options={statusOptions.map((v) => ({ label: v, value: v }))}
                  />
                )}
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'net_weight_kg']} label="Net Weight (kg)" style={itemStyle}>
                <InputNumber disabled={submitting || createAllState.running || activeLocked} style={{ width: '100%' }} min={0} step={0.001} stringMode />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'gross_weight_kg']} label="Gross Weight (kg)" style={itemStyle}>
                <InputNumber disabled={submitting || createAllState.running || activeLocked} style={{ width: '100%' }} min={0} step={0.001} stringMode />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'final_destination']} label="Final Destination" style={itemStyle} normalize={toUpperOrSame}>
                <Input disabled={submitting || createAllState.running || activeLocked} maxLength={50} />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'length_cm']} label="Length (cm)" style={itemStyle}>
                <InputNumber disabled={submitting || createAllState.running || activeLocked} style={{ width: '100%' }} min={0} step={0.001} stringMode />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'width_cm']} label="Width (cm)" style={itemStyle}>
                <InputNumber disabled={submitting || createAllState.running || activeLocked} style={{ width: '100%' }} min={0} step={0.001} stringMode />
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'height_cm']} label="Height (cm)" style={itemStyle}>
                <InputNumber disabled={submitting || createAllState.running || activeLocked} style={{ width: '100%' }} min={0} step={0.001} stringMode />
              </Form.Item>

              <Form.Item label="Freight Ton" style={itemStyle}>
                <Form.Item
                  noStyle
                  shouldUpdate={(p, n) =>
                    (p?.packages?.[activePackageIndex]?.gross_weight_kg !== n?.packages?.[activePackageIndex]?.gross_weight_kg) ||
                    (p?.packages?.[activePackageIndex]?.length_cm !== n?.packages?.[activePackageIndex]?.length_cm) ||
                    (p?.packages?.[activePackageIndex]?.width_cm !== n?.packages?.[activePackageIndex]?.width_cm) ||
                    (p?.packages?.[activePackageIndex]?.height_cm !== n?.packages?.[activePackageIndex]?.height_cm)
                  }
                >
                  {() => {
                    const gross = Number(form.getFieldValue(['packages', activePackageIndex, 'gross_weight_kg']) ?? 0);
                    const l = Number(form.getFieldValue(['packages', activePackageIndex, 'length_cm']) ?? 0);
                    const w = Number(form.getFieldValue(['packages', activePackageIndex, 'width_cm']) ?? 0);
                    const h = Number(form.getFieldValue(['packages', activePackageIndex, 'height_cm']) ?? 0);

                    const weightTon = Number.isFinite(gross) ? gross / 1000 : 0;
                    const volumeM3 = Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) ? (l * w * h) / 1_000_000 : 0;
                    const freightTon = Math.max(weightTon || 0, volumeM3 || 0);

                    return (
                      <Input
                        readOnly
                        value={Number.isFinite(freightTon) ? freightTon.toFixed(3) : ''}
                        style={{ background: '#f5f5f5', textAlign: 'right' }}
                      />
                    );
                  }}
                </Form.Item>
              </Form.Item>

              <Form.Item name={['packages', activePackageIndex, 'remarks']} label="Remarks" style={{ ...itemStyle, marginTop: 4 }} normalize={toUpperOrSame}>
                <Input disabled={submitting || createAllState.running || activeLocked} />
              </Form.Item>
            </Form>
          )}
        </Col>

        <Col flex="auto">
          <Card
            size="small"
            title={packageContentsTitle}
            bodyStyle={{ background: '#e6f4ff' }}
            style={{ marginTop: 6, background: '#e6f4ff' }}
            extra={
              <Space>
                {activeTabKey !== 'total' && activeItems?.length ? (
                  <div style={{ fontWeight: 600 }}>{`Total: ${itemsGrandTotal.toFixed(3)}`}</div>
                ) : null}
                <Button onClick={addItemRow} disabled={submitting || createAllState.running || activeTabKey === 'total' || activeLocked}>
                  Add Item
                </Button>
                <Button
                  onClick={() => plInputRef.current?.click()}
                  disabled={isEditMode || submitting || createAllState.running || activeTabKey === 'total' || activeLocked || plLoading}
                  loading={plLoading}
                >
                  Import PL Attachment
                </Button>
                <ExcelUploadButton
                  tableName="mat_logistic_package_items"
                  buttonText="Import Items"
                  disabled={submitting || createAllState.running || activeTabKey === 'total' || activeLocked}
                  templateFields={[
                    { header: 'Item ID', field: 'item_id' },
                    { header: 'Parent Item Code', field: 'parent_item_code' },
                    { header: 'Item Code', field: 'itemcode' },
                    { header: 'Size', field: 'size_text' },
                    { header: 'Short Description', field: 'short_description' },
                    { header: 'Long Description', field: 'long_description' },
                    { header: 'HS Code', field: 'hs_code' },
                    { header: 'Country of Origin', field: 'country_of_origin' },
                    { header: 'Unit', field: 'unit' },
                    { header: 'Unit Price', field: 'unit_price' },
                    { header: 'Currency', field: 'currency' },
                    { header: 'Qty', field: 'quantity' },
                    { header: 'Notes', field: 'notes' },
                  ]}
                  onLoaded={(r) => {
                    handleItemsExcelLoaded({ rows: r.rows });
                  }}
                />
              </Space>
            }
          >
            <StandardTable<PackageItemDraft>
              size="small"
              actionColumnPosition="first"
              pagination={{ pageSize: 20, showSizeChanger: true, hideOnSinglePage: false } as any}
              rowKey={(r) => r.key}
              dataSource={activeItems}
              scroll={{ x: 'max-content' }}
              columns={
                ([
                  { title: 'ID', dataIndex: 'item_id', key: 'item_id', width: 45, align: 'right' as const },
                  { title: 'Parent Code', dataIndex: 'parent_item_code', key: 'parent_item_code', width: 100 },
                  { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 100 },
                  { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 80 },
                  { title: 'Short Description', dataIndex: 'short_description', key: 'short_description', width: 180 },
                  { title: 'Long Description', dataIndex: 'long_description', key: 'long_description', width: 240 },
                  { title: 'HS', dataIndex: 'hs_code', key: 'hs_code', width: 80 },
                  { title: 'Origin', dataIndex: 'country_of_origin', key: 'country_of_origin', width: 120 },
                  { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 55 },
                  {title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price', width: 100,align: 'right' as const,
                        className: 'ant-table-column-align-right',
                        render: (v: any) => {
                          const n = typeof v === 'number' ? v : Number(String(v));
                          return Number.isFinite(n) ? n.toFixed(3) : '';
                        },
                  },
                  { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 80, render: (v: any) => (v ? String(v) : 'USD') },
                  {  title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 110, align: 'right' as const,
                    className: 'ant-table-column-align-right',
                    render: (v: any) => {
                      const n = typeof v === 'number' ? v : Number(String(v));
                      return Number.isFinite(n) ? n.toFixed(3) : '';
                    },
                  },
                  {
                    title: 'Total',dataIndex: 'total_price', key: 'total_price', width: 120, align: 'right' as const,
                    className: 'ant-table-column-align-right',
                    render: (_: any, r: PackageItemDraft) => {
                      const up = typeof r.unit_price === 'number' ? r.unit_price : Number(String(r.unit_price ?? 0));
                      const qty = typeof r.quantity === 'number' ? r.quantity : Number(String(r.quantity ?? 0));
                      const total = up * qty;
                      return <div>{Number.isFinite(total) ? total.toFixed(3) : ''}</div>;
                    },
                  },
                  { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 500 },
                  ...(!activeLocked && activeTabKey !== 'total' && !submitting && !createAllState.running
                    ? [
                        createActionColumn<PackageItemDraft>({
                          width: 72,
                          showView: false,
                          onEdit: (r) => editItemRow(r),
                          onDelete: (r) => removeItemRow(r.key),
                        }),
                      ]
                    : []),
                ] as ColumnType<PackageItemDraft>[])
              }
            />

            {activeTabKey !== 'total' ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Space wrap>
                  <FileUploadTool
                    ref={(r) => {
                      fileUploadRefsByIndex.current[activePackageIndex] = r;
                    }}
                    buttonText="Upload Files"
                    tempSubpath={`matlog/packages/${String(form.getFieldValue(['packages', activePackageIndex, 'package_id']) || '').trim() || `package_${activePackageIndex + 1}`}`}
                  />
                  {String(uploadPathByIndex?.[activePackageIndex] || '').trim() !== '' && (
                    <FileDownloadTool
                      buttonText={(<Button type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }}>Attached Files</Button>)}
                      contentPath={String(uploadPathByIndex?.[activePackageIndex] || '')}
                    />
                  )}
                </Space>
              </div>
            ) : null}
          </Card>
        </Col>
      </Row>

      <Drawer
        title={editingItemKey ? 'Edit Item' : 'Add Item'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingItemKey(null);
          setEditingPackageIndex(null);
          itemForm.resetFields();
        }}
        width={520}
        maskClosable={false}
        destroyOnClose
      >
        <div ref={drawerBodyRef}>
          <Form
            form={itemForm}
            layout="horizontal"
            colon={false}
            labelCol={{ flex: '140px' }}
            wrapperCol={{ flex: 'auto' }}
            labelAlign="left"
          >
            <Row gutter={[12, 0]}>
              <Col span={4}>
                <Form.Item name="item_id" label="Item ID">
                  <InputNumber style={{ width: '100%' }} min={1} step={1} stringMode />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Form.Item label="Parent Item Code">
                  <Space size={8} style={{ width: '100%', display: 'flex' }}>
                    <Form.Item name="parent_item_code" normalize={toUpperOrSame} style={{ marginBottom: 0, flex: 1 }}>
                      <Input style={{ width: '100%' }} maxLength={100} />
                    </Form.Item>
                    <Button
                      size="small"
                      onClick={() => {
                        setMaterialSelectTarget('parent_item_code');
                        setMaterialModalOpen(true);
                      }}
                    >
                      Select
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Item Code" required>
              <Space size={6} style={{ width: '100%', display: 'flex' }}>
                <Form.Item name="itemcode" rules={[{ required: true }]} normalize={toUpperOrSame} style={{ marginBottom: 0, flex: 1 }}>
                  <Input style={{ width: '100%' }} maxLength={50} />
                </Form.Item>
                <Button
                  size="small"
                  onClick={() => {
                    setMaterialSelectTarget('itemcode');
                    setMaterialModalOpen(true);
                  }}
                >
                  Select
                </Button>
              </Space>
            </Form.Item>
            <Form.Item name="size_text" label="Size" normalize={toUpperOrSame}>
              <Input style={{ width: '70%' }} maxLength={15} />
            </Form.Item>
            <Form.Item name="short_description" label="Short Description" normalize={toUpperOrSame}>
              <Input style={{ width: '70%' }} maxLength={120} />
            </Form.Item>
            <Form.Item name="long_description" label="Long Description" normalize={toUpperOrSame}>
              <Input style={{ width: '100%' }} maxLength={255} />
            </Form.Item>
            <Row gutter={[12, 0]}>
              <Col span={10}>
                <Form.Item name="hs_code" label="HS Code" normalize={toUpperOrSame}>
                  <Input style={{ width: '100%' }} maxLength={10} />
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item name="country_of_origin" label="Country of Origin" normalize={toUpperOrSame}>
                  <Input style={{ width: '100%' }} maxLength={50} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[0, 0]}>
              <Col span={4}>
                <Form.Item name="unit" label="Unit" normalize={toUpperOrSame} labelCol={{ flex: '60px' }} wrapperCol={{ flex: 'auto' }}>
                  <Input style={{ width: '90%' }} maxLength={10} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="unit_price" label="Unit Price" labelCol={{ flex: '80px' }} wrapperCol={{ flex: 'auto' }}>
                  <InputNumber style={{ width: '90%' }} min={0} step={0.001} stringMode controls={false} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="currency" label="Currency" labelCol={{ flex: '70px' }} wrapperCol={{ flex: 'auto' }}>
                  <Form.Item noStyle shouldUpdate={(p, n) => p.currency !== n.currency}>
                    {() => (
                      <Space size={6} style={{ width: '70%' }}>
                        <Input style={{ width: 74 }} readOnly value={itemForm.getFieldValue('currency') || 'USD'} />
                        <Button
                          size="small"
                          disabled={submitting || createAllState.running || activeLocked}
                          onClick={(e) => {
                            const rect = (e.currentTarget as HTMLButtonElement)?.getBoundingClientRect?.();
                            setCurrencyModalAnchorRect(rect ?? null);
                            setCurrencyModalOpen(true);
                          }}
                        >
                          Pick...
                        </Button>
                      </Space>
                    )}
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="quantity" label="Quantity">
              <InputNumber style={{ width: '50%' }} min={0} step={0.001} stringMode controls={false} />
            </Form.Item>
            <Form.Item name="notes" label="Notes" normalize={toUpperOrSame}>
              <Input.TextArea style={{ width: '100%' }} rows={4} />
            </Form.Item>

            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setDrawerOpen(false);
                  setEditingItemKey(null);
                  setEditingPackageIndex(null);
                  itemForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" onClick={saveDrawerItem} disabled={submitting || createAllState.running || activeLocked}>
                Save
              </Button>
            </Space>
          </Form>
        </div>
      </Drawer>

      <MatClntMasterMaterialSelect
        open={materialModalOpen}
        title="Select Material"
        onCancel={() => setMaterialModalOpen(false)}
        onOk={(rows: any[]) => {
          setMaterialModalOpen(false);
          const r = Array.isArray(rows) ? rows[0] : undefined;
          if (!r) return;
          if (materialSelectTarget === 'parent_item_code') {
            itemForm.setFieldsValue({
              parent_item_code: r.itemcode ?? null,
            });
            return;
          }
          itemForm.setFieldsValue({
            itemcode: r.itemcode ?? null,
            size_text: r.size_text ?? null,
            short_description: r.description ?? null,
            unit: r.unit ?? null,
          });
        }}
      />

      <CurrencySelect
        open={currencyModalOpen}
        value={itemForm.getFieldValue('currency')}
        anchorRect={currencyModalAnchorRect}
        onCancel={() => {
          setCurrencyModalOpen(false);
        }}
        afterClose={() => setCurrencyModalAnchorRect(null)}
        returnMode="currency_code"
        getContainer={() => drawerBodyRef.current}
        zIndex={2100}
        onOk={(picked) => {
          const code = String(picked || '').toUpperCase().slice(0, 3);
          itemForm.setFieldsValue({ currency: code || 'USD' });
          setCurrencyModalOpen(false);
        }}
      />

      <Modal
        open={plConflictOpen}
        title="Import conflict: package(s) already exist"
        onCancel={() => {
          setPlConflictOpen(false);
          const resolve = plConflictResolverRef.current;
          plConflictResolverRef.current = null;
          if (resolve) resolve('cancel');
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setPlConflictOpen(false);
                const resolve = plConflictResolverRef.current;
                plConflictResolverRef.current = null;
                if (resolve) resolve('cancel');
              }}
            >
              Cancel import
            </Button>
            <Button
              onClick={() => {
                setPlConflictOpen(false);
                const resolve = plConflictResolverRef.current;
                plConflictResolverRef.current = null;
                if (resolve) resolve('merge');
              }}
            >
              Merge items
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => {
                setPlConflictOpen(false);
                const resolve = plConflictResolverRef.current;
                plConflictResolverRef.current = null;
                if (resolve) resolve('replace');
              }}
            >
              Replace items
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 8 }}>The imported file contains package IDs that already exist on this screen.</div>
        <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
          Merge will append imported items to existing items. Replace will overwrite the existing item list for the overlapping packages only.
        </div>
        <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #f0f0f0', padding: 8, borderRadius: 6 }}>
          {(plConflictIds || []).map((id) => (
            <div key={id} style={{ padding: '2px 0' }}>
              {id}
            </div>
          ))}
        </div>
      </Modal>

      <input
        ref={plInputRef}
        type="file"
        accept=".xls,.xlsx"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handlePlTemplateFile(f);
        }}
      />
    </Card>
  );
};

export default MatLogPackageUpsert;

